/**
 * Heating Cost Calculation — Pure Functions
 *
 * Calculates annual heating costs for all fuel variants given useful heat demand.
 * No React dependencies — fully testable pure functions.
 *
 * Key formula:
 *   fuelConsumption = heatDemand / (efficiency × calorificValue × unitConversion)
 *   annualCost = fuelConsumption × pricePerUnit
 */

import type {
  FuelVariantDefinition,
  FuelVariantId,
  FuelPrice,
  FuelCostResult,
  HeatingCostComparison,
} from '../types/heatingCost.types';
import { FUEL_VARIANTS, FUEL_VARIANT_ORDER, WOOD_MP_TO_KG } from '../constants/fuelDefaults';

/**
 * Returns the number of kg (or liters, kWh) per one native fuel unit.
 * For most variants, calorificValue is per kg, but fuelUnit may be "tona" (1000 kg)
 * or "m.p." (spatial meter of wood ≈ 340 kg).
 */
function getUnitsPerFuelUnit(variant: FuelVariantDefinition): number {
  if (variant.fuelUnit === 'tona') return 1000;
  if (variant.fuelUnit === 'm.p.') return WOOD_MP_TO_KG;
  // For kWh, litr — calorificValue is already per fuel unit
  return 1;
}

/**
 * Calculate annual fuel consumption in native units.
 *
 * @param heatDemand - Total useful heat demand [kWh/year]
 * @param variant - Fuel variant definition
 * @returns Fuel consumption in native units (kWh, liters, tons, m.p.)
 */
export function calculateFuelConsumption(
  heatDemand: number,
  variant: FuelVariantDefinition
): number {
  if (heatDemand <= 0) return 0;
  if (variant.efficiency <= 0) return 0;

  const unitsPerFuel = getUnitsPerFuelUnit(variant);
  const effectiveCalorific = variant.calorificValue * unitsPerFuel;

  return heatDemand / (variant.efficiency * effectiveCalorific);
}

/**
 * Calculate annual cost from fuel consumption and price.
 *
 * @param consumption - Annual fuel consumption in native units
 * @param price - Fuel price definition
 * @returns Annual cost in PLN (gross)
 */
export function calculateAnnualCost(consumption: number, price: FuelPrice): number {
  return consumption * price.pricePerUnit;
}

/**
 * Calculate cost per kWh of useful heat.
 *
 * @param annualCost - Annual heating cost [PLN]
 * @param heatDemand - Total useful heat demand [kWh/year]
 * @returns Cost per kWh [PLN/kWh]
 */
export function calculateCostPerKwh(annualCost: number, heatDemand: number): number {
  if (heatDemand <= 0) return 0;
  return annualCost / heatDemand;
}

/**
 * Calculate heating costs for all fuel variants.
 *
 * @param spaceHeatingDemand - Useful energy for space heating [kWh/year]
 * @param hotWaterDemand - Useful energy for hot water [kWh/year]
 * @param prices - Fuel prices (may be user-modified)
 * @param currentSystemVariant - Optional: user's current heating system variant ID
 * @returns Complete comparison with all variants sorted by cost
 */
export function calculateAllHeatingCosts(
  spaceHeatingDemand: number,
  hotWaterDemand: number,
  prices: FuelPrice[],
  currentSystemVariant?: FuelVariantId
): HeatingCostComparison {
  const totalHeatDemand = spaceHeatingDemand + hotWaterDemand;
  const priceMap = new Map(prices.map(p => [p.variantId, p]));

  const results: FuelCostResult[] = FUEL_VARIANT_ORDER.map(variantId => {
    const variant = FUEL_VARIANTS[variantId];
    const price = priceMap.get(variantId)!;

    const consumption = calculateFuelConsumption(totalHeatDemand, variant);
    const annualCostGross = calculateAnnualCost(consumption, price);
    const annualCostNet = annualCostGross / (1 + variant.vatRate);
    const costPerKwh = calculateCostPerKwh(annualCostGross, totalHeatDemand);

    return {
      variant,
      price,
      annualCostNet,
      annualCostGross,
      costPerKwh,
      annualConsumption: consumption,
      isCurrentSystem: variantId === currentSystemVariant,
      isCheapest: false, // set after sorting
    };
  });

  // Sort by annual cost ascending
  results.sort((a, b) => a.annualCostGross - b.annualCostGross);

  // Mark cheapest
  if (results.length > 0 && totalHeatDemand > 0) {
    results[0].isCheapest = true;
  }

  const cheapestVariant = results[0]?.variant.id ?? 'gas_condensing_boiler';

  // Calculate savings vs current system
  let savingsVsCurrent: number | undefined;
  if (currentSystemVariant) {
    const currentResult = results.find(r => r.variant.id === currentSystemVariant);
    if (currentResult) {
      savingsVsCurrent = currentResult.annualCostGross - results[0].annualCostGross;
    }
  }

  return {
    totalHeatDemand,
    spaceHeatingDemand,
    hotWaterDemand,
    results,
    cheapestVariant,
    currentSystemVariant,
    savingsVsCurrent,
  };
}

/**
 * Map HeatingSystem (from simulator input) to FuelVariantId.
 *
 * The simulator uses different naming than the heating cost module.
 * Returns undefined if no mapping exists.
 */
export function mapHeatingSystemToVariant(heatingSystem: string): FuelVariantId | undefined {
  const mapping: Record<string, FuelVariantId> = {
    // Gas
    gas_old: 'gas_old_boiler',
    gas_traditional: 'gas_traditional_boiler',
    gas_condensing: 'gas_condensing_boiler',
    // LPG
    lpg_condensing: 'lpg_condensing_boiler',
    // Oil
    oil_traditional: 'oil_traditional_boiler',
    oil_condensing: 'oil_condensing_boiler',
    // Coal
    coal: 'coal_stoker',
    coal_old: 'coal_stoker',
    coal_ecopea: 'coal_ecopea',
    // Wood / Biomass
    biomass: 'wood_pellet_boiler',
    wood_log: 'wood_log_boiler',
    wood_pellet: 'wood_pellet_boiler',
    // Electricity
    heat_pump_ground: 'heat_pump_ground',
    heat_pump_air: 'heat_pump_air',
    electric_radiators: 'electric_boiler',
    electric_floor: 'electric_boiler',
    storage_heater: 'storage_heater',
    electric_boiler: 'electric_boiler',
    // District heating
    district_heating: 'district_heating',
    // Stove (piec kaflowy/kominek)
    stove: 'coal_stoker',
  };

  return mapping[heatingSystem];
}
